import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Button from '../common/Button';
import ToastContainer from '../common/ToastContainer';
import DashboardService from '../../services/dashboardService';
import AppointmentService from '../../services/appointmentService';
import SupplyService from '../../services/supplyService';
import { useToast } from '../../hooks/useToast';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import useOrganizationFeatureTag from '../../hooks/useOrganizationFeatureTag';
import { useAuthSync } from '../../hooks/useAuthSync';
import { getTodayDateString, formatDateTimeInTimezone } from '../../utils/dateUtils';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { timezone, isLoading: timezoneLoading } = useOrganizationTimezone();
  const { isEnabled: isWhatsAppEnabled, isLoading: isWhatsAppFeatureLoading } = useOrganizationFeatureTag('whatsapp_enabled');
  const { toasts, removeToast, success: showSuccess, error: showError, warning: showWarning } = useToast();
  const { hasPermission } = useAuthSync();
  const canViewFinancialReports = hasPermission('reports:viewfinancial');
  const canViewMedicalData = hasPermission('patient:viewmedicaldata');
  const [dashboardStats, setDashboardStats] = useState({
    totalPatients: 0,
    totalProcedures: 0,
    appointmentsToday: 0,
    dailyRevenue: 0
  });
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [appointmentsError, setAppointmentsError] = useState(null);
  const [tomorrowScheduledAppointments, setTomorrowScheduledAppointments] = useState([]);
  const [tomorrowAppointmentsLoading, setTomorrowAppointmentsLoading] = useState(false);
  const [tomorrowAppointmentsError, setTomorrowAppointmentsError] = useState(null);
  const [sendingTomorrowReminders, setSendingTomorrowReminders] = useState(false);
  const [expiringSupplies, setExpiringSupplies] = useState([]);
  const [suppliesLoading, setSuppliesLoading] = useState(false);
  const [suppliesError, setSuppliesError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState({
    show: false,
    appointmentId: null,
    newStatus: null,
    patientName: '',
    patientId: null
  });
  const [completedPatientId, setCompletedPatientId] = useState(null);
  const [updatingAppointment, setUpdatingAppointment] = useState(null);
  const [showAllAppointments, setShowAllAppointments] = useState(false);

  // Ref to prevent duplicate API calls
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const appointmentsLoadingRef = useRef(false);
  const lastAppointmentsLoadRef = useRef(null);
  const tomorrowAppointmentsLoadingRef = useRef(false);
  const lastTomorrowAppointmentsLoadRef = useRef(null);

  // Memoize auth token to prevent unnecessary effect triggers
  const authToken = useMemo(() => auth.user?.access_token, [auth.user?.access_token]);
  const tomorrowDate = useMemo(
    () => DateTime.now().setZone(timezone || 'America/Mexico_City').plus({ days: 1 }).toISODate(),
    [timezone]
  );
  const tomorrowAppointmentsWithPhoneCount = useMemo(
    () => tomorrowScheduledAppointments.filter((appointment) => appointment.patient?.cellPhone || appointment.patient?.phoneNumber || appointment.patient?.phone).length,
    [tomorrowScheduledAppointments]
  );

  const formatExpireDate = (isoOrDateString) => {
    if (!isoOrDateString) return '-';
    const d = new Date(isoOrDateString);
    if (Number.isNaN(d.getTime())) return '-';
    const day = d.toLocaleString('es-MX', { day: '2-digit', timeZone: 'UTC' });
    const month = d.toLocaleString('es-MX', { month: 'short', timeZone: 'UTC' }).replace('.', '').toLowerCase();
    const year = d.toLocaleString('es-MX', { year: 'numeric', timeZone: 'UTC' });
    return `${day} ${month} ${year}`;
  };

  const daysUntil = (isoOrDateString) => {
    if (!isoOrDateString) return null;
    const now = new Date();
    const d = new Date(isoOrDateString);
    if (Number.isNaN(d.getTime())) return null;
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.ceil((d.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0)) / msPerDay);
  };

  const formatDashboardAppointment = useCallback((appointment, activeTimezone) => {
    const patientFirstName = appointment.patient?.firstName || appointment.firstName || '';
    const patientLastName = appointment.patient?.lastName || appointment.lastName || '';
    const patientName = `${patientFirstName} ${patientLastName}`.trim() || 'Paciente no especificado';

    let date = 'No especificado';
    let time = 'No especificado';

    if (appointment.appointmentDateTime) {
      const formattedDateTime = formatDateTimeInTimezone(appointment.appointmentDateTime, activeTimezone);
      date = formattedDateTime.date;
      time = formattedDateTime.time;
    }

    return {
      ...appointment,
      patientName,
      date,
      time
    };
  }, []);

  // Memoized function to load dashboard statistics
  const loadDashboardStats = useCallback(async (token) => {
    // Prevent duplicate calls
    if (isLoadingRef.current || hasLoadedRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      setError(null);

      const stats = await DashboardService.getDashboardStats(token);
      setDashboardStats(stats);
      hasLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      setError('Error al cargar las estadísticas');
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, []);

  // Load dashboard statistics only once when auth token is available
  useEffect(() => {
    if (authToken && !hasLoadedRef.current) {
      loadDashboardStats(authToken);
    }
  }, [authToken, loadDashboardStats]);

  // Memoized function to load today's appointments
  const loadTodayAppointments = useCallback(async (token, timezone) => {
    // Create a key to track if we've already loaded for this combination
    const loadKey = `${token}-${timezone}`;

    // Prevent duplicate calls for the same token/timezone combination
    if (appointmentsLoadingRef.current || lastAppointmentsLoadRef.current === loadKey) {
      return;
    }

    try {
      appointmentsLoadingRef.current = true;
      setAppointmentsLoading(true);
      setAppointmentsError(null);

      const today = getTodayDateString(timezone);
      const appointmentsData = await AppointmentService.getAppointmentsByDate(today, token);

      // Enrich appointments with patient names and time formatting
      const enrichedAppointments = appointmentsData.map((appointment) => formatDashboardAppointment(appointment, timezone));

      // Filter appointments to show only scheduled and confirmed ones
      const filteredAppointments = enrichedAppointments.filter(appointment => {
        const status = appointment.status?.toLowerCase();
        return status === 'scheduled' || status === 'confirmed';
      });

      // Sort by time and take only the next 5
      const sortedAppointments = filteredAppointments
        .sort((a, b) => a.time.localeCompare(b.time))
        .slice(0, 5);

      setTodayAppointments(sortedAppointments);
      lastAppointmentsLoadRef.current = loadKey;
    } catch (error) {
      console.error('Error loading today appointments:', error);
      setAppointmentsError('Error al cargar las citas de hoy');
    } finally {
      setAppointmentsLoading(false);
      appointmentsLoadingRef.current = false;
    }
  }, [formatDashboardAppointment]);

  const loadTomorrowScheduledAppointments = useCallback(async (token, activeTimezone) => {
    const loadKey = `${token}-${activeTimezone}`;

    if (tomorrowAppointmentsLoadingRef.current || lastTomorrowAppointmentsLoadRef.current === loadKey) {
      return;
    }

    try {
      tomorrowAppointmentsLoadingRef.current = true;
      setTomorrowAppointmentsLoading(true);
      setTomorrowAppointmentsError(null);

      const appointmentsData = await AppointmentService.getAppointmentsByDate(tomorrowDate, token);
      const scheduledAppointments = appointmentsData
        .map((appointment) => formatDashboardAppointment(appointment, activeTimezone))
        .filter((appointment) => appointment.status?.toLowerCase() === 'scheduled')
        .sort((a, b) => new Date(a.appointmentDateTime) - new Date(b.appointmentDateTime));

      setTomorrowScheduledAppointments(scheduledAppointments);
      lastTomorrowAppointmentsLoadRef.current = loadKey;
    } catch (loadError) {
      console.error('Error loading tomorrow scheduled appointments:', loadError);
      setTomorrowAppointmentsError('Error al cargar las citas de mañana');
    } finally {
      setTomorrowAppointmentsLoading(false);
      tomorrowAppointmentsLoadingRef.current = false;
    }
  }, [formatDashboardAppointment, tomorrowDate]);

  // Load today's appointments when auth token and preferences are available
  useEffect(() => {
    if (authToken && timezone && !timezoneLoading) {
      loadTodayAppointments(authToken, timezone);
    }
  }, [authToken, timezone, timezoneLoading, loadTodayAppointments]);

  useEffect(() => {
    if (authToken && timezone && !timezoneLoading) {
      loadTomorrowScheduledAppointments(authToken, timezone);
    }
  }, [authToken, timezone, timezoneLoading, loadTomorrowScheduledAppointments]);

  // Load supplies and compute expiring list
  useEffect(() => {
    const loadSupplies = async () => {
      if (!authToken) return;
      try {
        setSuppliesLoading(true);
        setSuppliesError(null);
        const resp = await SupplyService.getAllSupplies(authToken);
        const items = resp.data || [];
        const withExpiry = items.filter(s => s.expirationDate);
        const annotated = withExpiry.map(s => ({
          ...s,
          du: daysUntil(s.expirationDate)
        }));
        const filtered = annotated.filter(s => s.du !== null && s.du <= 30);
        const sorted = filtered.sort((a, b) => (a.du ?? 9999) - (b.du ?? 9999)).slice(0, 5);
        setExpiringSupplies(sorted);
      } catch (e) {
        setSuppliesError('Error al cargar insumos');
      } finally {
        setSuppliesLoading(false);
      }
    };
    loadSupplies();
  }, [authToken]);

  const handleNewPatient = () => {
    navigate('/patients/new');
  };

  const handleNewAppointment = () => {
    navigate('/appointments/new');
  };

  const handleNewPayment = () => {
    navigate('/payments/new');
  };

  const handleViewReports = () => {
    navigate('/reports/financial');
  };

  const handleViewPatients = () => {
    navigate('/patients/list');
  };

  const handleViewAppointments = () => {
    navigate('/appointments');
  };

  const handleSupplyClick = () => {
    navigate('/supplies?expiring=true');
  };

  const handleSendTomorrowReminders = async () => {
    if (!authToken) {
      return;
    }

    if (tomorrowScheduledAppointments.length === 0) {
      showWarning('No hay citas pendientes por recordar para mañana.');
      return;
    }

    if (tomorrowAppointmentsWithPhoneCount === 0) {
      showWarning('Las citas de mañana no tienen un celular registrado para enviar WhatsApp.');
      return;
    }

    try {
      setSendingTomorrowReminders(true);

      const result = await AppointmentService.sendWhatsAppReminders(tomorrowDate, authToken);
      const successMessage = result.failedCount > 0
        ? `Se enviaron ${result.sentCount} recordatorios. ${result.failedCount} fallaron y ${result.skippedCount} se omitieron por falta de celular.`
        : `Se enviaron ${result.sentCount} recordatorios por WhatsApp.`;

      showSuccess(successMessage, 6000);
      lastTomorrowAppointmentsLoadRef.current = null;
      await loadTomorrowScheduledAppointments(authToken, timezone || 'America/Mexico_City');
    } catch (sendError) {
      console.error('Error sending tomorrow reminders:', sendError);
      showError(sendError.message || 'No se pudieron enviar los recordatorios por WhatsApp.');
    } finally {
      setSendingTomorrowReminders(false);
    }
  };

  const handlePatientClick = (patientId) => {
    if (patientId) {
      navigate(`/patients/${patientId}`);
    }
  };

  const handleStatusChange = (appointmentId, newStatus, patientName, patientId) => {
    setConfirmationDialog({
      show: true,
      appointmentId,
      newStatus,
      patientName,
      patientId: patientId || null
    });
  };

  const confirmStatusChange = async () => {
    const { appointmentId, newStatus, patientId } = confirmationDialog;
    setUpdatingAppointment(appointmentId);

    try {
      const token = auth.user?.access_token;

      await AppointmentService.updateAppointment(
        appointmentId,
        { status: newStatus },
        token
      );

      lastAppointmentsLoadRef.current = null;

      if (authToken && timezone) {
        await loadTodayAppointments(authToken, timezone);
      }

      if (newStatus === 'completed' && patientId && canViewMedicalData) {
        setCompletedPatientId(patientId);
      }
    } catch (error) {
      console.error('Error updating appointment status:', error);
    } finally {
      setUpdatingAppointment(null);
      setConfirmationDialog({ show: false, appointmentId: null, newStatus: null, patientName: '', patientId: null });
    }
  };

  const cancelStatusChange = () => {
    setConfirmationDialog({ show: false, appointmentId: null, newStatus: null, patientName: '' });
  };

  const statusLabels = {
    active: 'Activo',
    completed: 'Completado',
    cancelled: 'Cancelado',
    archived: 'Archivado',
    scheduled: 'Programada',
    confirmed: 'Confirmada',
    'no-show': 'No se presentó'
  };

  return (
    <div className="dashboard">
      {/* Content Area */}
      <main className="dashboard-content">
        <div className="content-wrapper">

          {/* Próximas Citas de Hoy - PRIMERO (Lo más importante) */}
          <div className="next-appointments priority-section">
            <h3>Próximas Citas de Hoy</h3>
            {appointmentsLoading ? (
              <div className="dashboard-appointments-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Cargando citas...</p>
              </div>
            ) : appointmentsError ? (
              <div className="dashboard-appointments-error">
                <i className="fas fa-exclamation-triangle"></i>
                <p>{appointmentsError}</p>
              </div>
            ) : todayAppointments.length === 0 ? (
              <div className="dashboard-no-appointments">
                <i className="fas fa-calendar-check"></i>
                <p>No hay más citas programadas para hoy</p>
              </div>
            ) : (
              <div className="today-appointments-list">
                {todayAppointments.slice(0, showAllAppointments ? undefined : 3).map((appointment, index) => (
                  <div
                    key={appointment.id || index}
                    className="today-appointment-card"
                    onClick={() => handlePatientClick(appointment.patientId)}
                    style={{ cursor: appointment.patientId ? 'pointer' : 'default' }}
                  >
                    <div className="today-appointment-top">
                      <div className="today-appointment-time">
                        <i className="fas fa-clock"></i>
                        <span>{appointment.time}</span>
                      </div>
                      <div className="today-appointment-status">
                        <span className={`today-status-badge ${appointment.status?.toLowerCase() || 'scheduled'}`}>
                          {statusLabels[appointment.status?.toLowerCase()] || 'Programada'}
                        </span>
                      </div>
                    </div>
                    <div className="today-appointment-details">
                      <div className="today-patient-name">
                        <i className="fas fa-user"></i>
                        <span>{appointment.patientName || 'Paciente no especificado'}</span>
                      </div>
                      {appointment.procedure && (
                        <div className="today-appointment-procedure">
                          <i className="fas fa-tooth"></i>
                          <span>{appointment.procedure}</span>
                        </div>
                      )}
                      {appointment.doctor && (
                        <div className="today-appointment-doctor">
                          <i className="fas fa-user-md"></i>
                          <span>{appointment.doctor.name}</span>
                        </div>
                      )}
                    </div>
                    <div className="dashboard-appointment-actions">
                      <button
                        className="dashboard-appointment-action-btn success"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleStatusChange(appointment.id, 'completed', appointment.patientName, appointment.patientId);
                        }}
                        disabled={updatingAppointment === appointment.id}
                        title="Marcar como completada"
                      >
                        <i className="fas fa-check"></i>
                      </button>
                      <button
                        className="dashboard-appointment-action-btn warning"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleStatusChange(appointment.id, 'no-show', appointment.patientName);
                        }}
                        disabled={updatingAppointment === appointment.id}
                        title="Marcar como no se presentó"
                      >
                        <i className="fas fa-user-times"></i>
                      </button>
                      {appointment.patientId && canViewMedicalData && (
                        <button
                          className="dashboard-appointment-action-btn info"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/patients/${appointment.patientId}/evolution-notes`);
                          }}
                          title="Nota de evolución"
                        >
                          <i className="fas fa-notes-medical"></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {todayAppointments.length > 3 && (
                  <div className="view-all-appointments">
                    <Button
                      variant="outline"
                      onClick={() => setShowAllAppointments(!showAllAppointments)}
                      className="view-all-btn"
                    >
                      {showAllAppointments ? 'Ver menos' : 'Ver todas las citas'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {isWhatsAppEnabled && (
          <div className="tomorrow-reminders-panel">
            <div className="tomorrow-reminders-copy">
              <span className="reminder-emoji" role="img" aria-label="alerta">⚠️</span>
              {tomorrowAppointmentsLoading ? (
                <p>Estamos revisando las citas de mañana.</p>
              ) : tomorrowAppointmentsError ? (
                <p>{tomorrowAppointmentsError}</p>
              ) : tomorrowScheduledAppointments.length > 0 ? (
                <p>
                  Tienes {tomorrowScheduledAppointments.length} {tomorrowScheduledAppointments.length === 1 ? 'cita programada' : 'citas programadas'} sin confirmar para mañana.
                </p>
              ) : (
                <p>No hay citas pendientes de confirmar para mañana.</p>
              )}
            </div>
            <div className="tomorrow-reminders-actions">
              <p className="tomorrow-reminders-meta">
                {tomorrowAppointmentsWithPhoneCount > 0
                  ? `Se enviará WhatsApp a ${tomorrowAppointmentsWithPhoneCount} ${tomorrowAppointmentsWithPhoneCount === 1 ? 'paciente con celular registrado' : 'pacientes con celular registrado'}.`
                  : 'No hay celulares registrados para enviar recordatorios.'}
              </p>
              <Button
                variant="warning"
                size="sm"
                className="tomorrow-reminders-btn"
                onClick={handleSendTomorrowReminders}
                loading={sendingTomorrowReminders}
                disabled={isWhatsAppFeatureLoading || tomorrowAppointmentsLoading || Boolean(tomorrowAppointmentsError) || tomorrowScheduledAppointments.length === 0 || tomorrowAppointmentsWithPhoneCount === 0}
                icon="fab fa-whatsapp"
              >
                Enviar recordatorio
              </Button>
            </div>
          </div>
        )}

          {/* Quick Actions */}
          <div className="quick-actions">
            <h3>Acciones Rápidas</h3>
            <div className="actions-grid">
              <Button
                variant="outline"
                icon="fas fa-user-plus"
                className="dashboard-action-btn"
                onClick={handleNewPatient}
              >
                Nuevo Paciente
              </Button>
              <Button
                variant="outline"
                icon="fas fa-calendar-plus"
                className="dashboard-action-btn"
                onClick={handleNewAppointment}
              >
                Nueva Cita
              </Button>
              <Button
                variant="outline"
                icon="fas fa-file-invoice"
                className="dashboard-action-btn"
                onClick={handleNewPayment}
              >
                Registrar pago
              </Button>
              {canViewFinancialReports && (
              <Button
                variant="outline"
                icon="fas fa-chart-bar"
                className="dashboard-action-btn"
                onClick={handleViewReports}
              >
                Ver Reportes Financieros
              </Button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card" onClick={handleViewPatients} style={{ cursor: 'pointer' }}>
              <div className="stat-icon patients">
                <i className="fas fa-users"></i>
              </div>
              <div className="stat-info">
                <h3>{isLoading ? '...' : dashboardStats.totalPatients.toLocaleString()}</h3>
                <p>Pacientes</p>
                {error && <small style={{ color: 'red', fontSize: '0.8em' }}>Error al cargar</small>}
              </div>
            </div>

            <div className="stat-card" onClick={handleViewAppointments} style={{ cursor: 'pointer' }}>
              <div className="stat-icon appointments">
                <i className="fas fa-calendar"></i>
              </div>
              <div className="stat-info">
                <h3>{isLoading ? '...' : dashboardStats.appointmentsToday.toLocaleString()}</h3>
                <p>Citas Hoy</p>
                {error && <small style={{ color: 'red', fontSize: '0.8em' }}>Error al cargar</small>}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon revenue">
                <i className="fas fa-dollar-sign"></i>
              </div>
              <div className="stat-info">
                <h3>
                  {isLoading
                    ? '...'
                    : (canViewFinancialReports
                        ? `$${(dashboardStats.dailyRevenue || 0).toLocaleString()}`
                        : '$-')}
                </h3>
                <p>Ingresos del día</p>
                {error && <small style={{ color: 'red', fontSize: '0.8em' }}>Error al cargar</small>}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon treatments">
                <i className="fas fa-tooth"></i>
              </div>
              <div className="stat-info">
                <h3>{isLoading ? '...' : dashboardStats.totalProcedures.toLocaleString()}</h3>
                <p>Procedimientos</p>
                {error && <small style={{ color: 'red', fontSize: '0.8em' }}>Error al cargar</small>}
              </div>
            </div>
          </div>

          {/* Expiring Supplies */}
          <div className="expiring-supplies">
            <div className="section-header">
              <h3>Próximos insumos a caducar</h3>
              <h4 className="subheader-text">≤ 30 días</h4>
            </div>
            {suppliesLoading ? (
              <div className="dashboard-appointments-loading">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : suppliesError ? (
              <div className="dashboard-appointments-error">
                <i className="fas fa-exclamation-triangle"></i>
                <p>{suppliesError}</p>
              </div>
            ) : expiringSupplies.length === 0 ? (
              <div className="dashboard-no-appointments">
                <i className="fas fa-check-circle"></i>
                <p>No hay insumos por caducar en los próximos 30 días</p>
              </div>
            ) : (
              <div className="expiring-supplies-list">
                {expiringSupplies.map((s) => (
                  <div 
                    key={s.id} 
                    className="expiring-supply-item" 
                    onClick={handleSupplyClick} 
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="expiring-supply-name">
                      <i className="fas fa-box"></i>
                      <span>{s.name}</span>
                    </div>
                    <div className="expiring-supply-date">
                      <span className="date">{formatExpireDate(s.expirationDate)}</span>
                      {s.du < 0 ? (
                        <span className="badge badge-danger">Vencido</span>
                      ) : (
                        <span className="badge badge-warning">Caduca en {s.du} días</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </main>

      {/* Confirmation Dialog */}
      {confirmationDialog.show && (
        <div className="dashboard-modal-overlay" onClick={cancelStatusChange}>
          <div className="dashboard-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h3>Confirmar cambio de estado</h3>
              <button className="dashboard-modal-close" onClick={cancelStatusChange}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="dashboard-modal-body">
              <p>
                ¿Estás seguro de que quieres marcar la cita de{' '}
                <strong>{confirmationDialog.patientName}</strong> como{' '}
                <strong>
                  {confirmationDialog.newStatus === 'completed' ? 'completada' : 'no se presentó'}
                </strong>?
              </p>
            </div>
            <div className="dashboard-modal-actions">
              <Button
                variant="secondary"
                onClick={cancelStatusChange}
                disabled={updatingAppointment !== null}
              >
                Cancelar
              </Button>
              <Button
                variant={confirmationDialog.newStatus === 'completed' ? 'success' : 'warning'}
                onClick={confirmStatusChange}
                loading={updatingAppointment !== null}
                icon={confirmationDialog.newStatus === 'completed' ? 'fas fa-check' : 'fas fa-user-times'}
              >
                {updatingAppointment !== null ? 'Actualizando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Post-complete: offer to add evolution note */}
      {completedPatientId && (
        <div className="dashboard-modal-overlay" onClick={() => setCompletedPatientId(null)}>
          <div className="dashboard-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h3>Cita completada</h3>
              <button className="dashboard-modal-close" onClick={() => setCompletedPatientId(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="dashboard-modal-body">
              <p>¿Deseas agregar una <strong>nota de evolución</strong> para este paciente?</p>
            </div>
            <div className="dashboard-modal-actions">
              <Button variant="secondary" onClick={() => setCompletedPatientId(null)}>
                Ahora no
              </Button>
              <Button
                variant="primary"
                icon="fas fa-notes-medical"
                onClick={() => {
                  navigate(`/patients/${completedPatientId}/evolution-notes/new`);
                  setCompletedPatientId(null);
                }}
              >
                Agregar nota
              </Button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default Dashboard; 